import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_post-planner-51/artifacts/17buinbo_AFA%20transparent.png";

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="register-page">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex lg:flex-1 bg-[#1a3a4a] items-center justify-center p-12">
        <div className="max-w-md">
          <div className="w-24 h-24 bg-[#c5d93d]/20 rounded-full flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-6">Start Planning Today</h2>
          
          <div className="space-y-4">
            {[
              'Monthly calendar view for all your posts',
              'Import posts from CSV instantly',
              'Upload images including HEIC from iPhone',
              'Easy drag and drop to reschedule'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-white/80">
                <div className="w-5 h-5 bg-[#c5d93d] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#1a3a4a]" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1a3a4a]/60 hover:text-[#1a3a4a] mb-8 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>

          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img src={LOGO_URL} alt="Post Planner" className="h-10 w-10 object-contain" />
            <span className="font-bold text-xl text-[#1a3a4a]">Post Planner</span>
          </div>

          <h1 className="text-3xl font-black text-[#1a3a4a] tracking-tight mb-2">Create your account</h1>
          <p className="text-[#1a3a4a]/60 mb-8">Start organizing your social media calendar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Name
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                className="border-[#1a3a4a]/15 focus:border-[#c5d93d] h-12"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="border-[#1a3a4a]/15 focus:border-[#c5d93d] h-12"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  className="border-[#1a3a4a]/15 focus:border-[#c5d93d] h-12 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1a3a4a]/40 hover:text-[#1a3a4a]/60"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary h-12 rounded-md text-base"
              data-testid="register-submit-btn"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#1a3a4a]/60">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2b7a8a] hover:text-[#1a3a4a] font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
